/**
 * Active course context is stored in localStorage to support multiple courses
 * while avoiding hardcoded defaults (existing single-course users still resolve
 * to the NSW Selective course via default selection rules elsewhere).
 */

const STORAGE_KEY = 'activeCourseId';

const getStorage = () => {
    if (typeof window === 'undefined') {
        return null;
    }
    try {
        return window.localStorage;
    } catch (error) {
        return null;
    }
};

/**
 * A course id is a Mongo ObjectId: 24 hex characters. Accepted case-insensitively,
 * but always STORED and RETURNED lowercase.
 *
 * Case matters more than it looks. Entitlement is decided by string comparison against
 * `subscription.courseId.toString()`, which mongoose renders lowercase, so an uppercase
 * id is a perfectly valid ObjectId that silently matches nothing — an entitled user
 * would be told they are not entitled. Canonicalising at the storage boundary means no
 * caller has to remember this.
 */
export const isValidCourseId = (value) => (
    typeof value === 'string' && /^[0-9a-fA-F]{24}$/.test(value.trim())
);

export const canonicalizeCourseId = (value) => (
    isValidCourseId(value) ? value.trim().toLowerCase() : ''
);

export const getActiveCourseId = () => {
    const storage = getStorage();
    if (!storage) {
        return '';
    }
    try {
        const stored = storage.getItem(STORAGE_KEY) || '';
        if (!stored) {
            return '';
        }
        const canonical = canonicalizeCourseId(stored);
        if (!canonical) {
            // Drop corrupt state rather than handing it to callers: it is now sent to
            // the API as ?courseId, where an invalid value is a 400.
            storage.removeItem(STORAGE_KEY);
            return '';
        }
        if (canonical !== stored) {
            storage.setItem(STORAGE_KEY, canonical);
        }
        return canonical;
    } catch (error) {
        return '';
    }
};

export const setActiveCourseId = (courseId) => {
    const storage = getStorage();
    const canonical = canonicalizeCourseId(courseId);
    if (!storage || !canonical) {
        return;
    }
    try {
        storage.setItem(STORAGE_KEY, canonical);
    } catch (error) {
        // Swallow storage errors to avoid blocking UI.
    }
};

export const selectDefaultCourse = (courses = []) => {
    if (!Array.isArray(courses) || courses.length === 0) {
        return null;
    }

    const bySlug = courses.find((course) => {
        const slug = (course?.slug || '').toString().trim().toLowerCase();
        return slug === 'nsw-selective';
    });
    if (bySlug) {
        return bySlug;
    }

    const byDefaultFlag = courses.find((course) => course?.isDefault === true);
    if (byDefaultFlag) {
        return byDefaultFlag;
    }

    return courses[0] || null;
};
